import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Settings, Save, Building2, Banknote, Lock, KeyRound, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PinLockGate, lockPin } from "@/components/PinLockGate";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DENOMINATIONS = [1, 2, 3, 5, 10, 20, 30, 50];
const DENOM_LABELS = { 1: '$1', 2: '$2', 3: '$3', 5: '$5', 10: '$10', 20: '$20', 30: '$30', 50: '$50' };
const FADE_IN = { initial: { opacity: 0 }, animate: { opacity: 1 } };

export default function UserSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [packSizes, setPackSizes] = useState({ 1: 300, 2: 150, 3: 100, 5: 100, 10: 60, 20: 30, 30: 30, 50: 30 });
  const [showQtyCells, setShowQtyCells] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  // Business / company info shown on all printed documents
  const [profile, setProfile] = useState({
    company_name: "",
    dba_name: "",
    business_address: "",
    phone_number: "",
    ein: "",
  });

  // Change-PIN state
  const [pinCurrent, setPinCurrent] = useState("");
  const [pinNew, setPinNew] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [changingPin, setChangingPin] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [settingsRes, profileRes] = await Promise.all([
        axios.get(`${API}/user/settings`),
        axios.get(`${API}/user/profile`),
      ]);
      if (settingsRes.data.pack_sizes) {
        const ps = {};
        DENOMINATIONS.forEach(d => {
          ps[d] = parseInt(settingsRes.data.pack_sizes[d]) || packSizes[d];
        });
        setPackSizes(ps);
      }
      if (typeof settingsRes.data.show_qty_cells === 'boolean') {
        setShowQtyCells(settingsRes.data.show_qty_cells);
      }
      setProfile({
        company_name: profileRes.data.company_name || "",
        dba_name: profileRes.data.dba_name || "",
        business_address: profileRes.data.business_address || profileRes.data.store_address || "",
        phone_number: profileRes.data.phone_number || "",
        ein: profileRes.data.ein || "",
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error("Failed to fetch settings:", err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/user/profile`, profile);
      toast.success("Business info saved — will appear on all printed documents");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save business info");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePackSizes = async () => {
    setSaving(true);
    try {
      const sized = {};
      DENOMINATIONS.forEach(d => { sized[String(d)] = parseInt(packSizes[d]) || 30; });
      await axios.put(`${API}/user/settings`, { pack_sizes: sized, show_qty_cells: showQtyCells });
      toast.success("Settings saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleShowQtyCells = async (checked) => {
    const next = !!checked;
    setShowQtyCells(next);
    try {
      await axios.put(`${API}/user/settings`, { show_qty_cells: next });
      toast.success(next ? "Pack quantity cells shown" : "Pack quantity cells hidden");
    } catch (err) {
      setShowQtyCells(!next); // revert on failure
      toast.error(err.response?.data?.detail || "Failed to update setting");
    }
  };

  const changePin = async () => {
    if (!/^\d{4}$/.test(pinCurrent)) { toast.error("Enter your current 4-digit PIN"); return; }
    if (!/^\d{4}$/.test(pinNew)) { toast.error("New PIN must be 4 digits"); return; }
    if (pinNew !== pinConfirm) { toast.error("New PINs do not match"); return; }
    setChangingPin(true);
    try {
      await axios.post(`${API}/auth/pin/set`, { current_pin: pinCurrent, new_pin: pinNew });
      toast.success("PIN updated");
      setPinCurrent(""); setPinNew(""); setPinConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update PIN");
    } finally {
      setChangingPin(false);
    }
  };

  const lockNow = () => {
    lockPin();
    toast.info("Settings locked");
    navigate("/");
  };

  if (loading) {
    return (
      <PinLockGate label="Settings">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </PinLockGate>
    );
  }

  return (
    <PinLockGate label="Settings">
    <motion.div {...FADE_IN} className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-mono font-bold">Settings</h1>
        <Button
          onClick={lockNow}
          variant="ghost"
          size="sm"
          className="font-mono text-xs gap-1.5"
          data-testid="lock-settings-now"
          title="Lock Settings now and exit"
        >
          <Lock className="w-3.5 h-3.5" />
          Lock now
        </Button>
      </div>

      {/* LVM quick-open button (gated behind PIN) */}
      <Card className="border-border/50 bg-gradient-to-br from-emerald-900/30 to-emerald-950/10" data-testid="lvm-launcher-card">
        <CardContent className="p-5 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Banknote className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="font-mono font-bold text-card-foreground">LVM — Cash & Prize Report</p>
              <p className="font-mono text-xs text-muted-foreground leading-snug">
                Daily cash count plus online/instant sales reconciliation. Hidden from the main navigation; only reachable from Settings.
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/lvm")}
            className="btn-primary flex-shrink-0"
            data-testid="open-lvm-btn"
          >
            Open LVM
          </Button>
        </CardContent>
      </Card>

      {/* Business / Company info — appears on all printed documents */}
      <Card className="border-border/50" data-testid="business-info-panel">
        <CardHeader>
          <CardTitle className="font-mono flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Business Information
          </CardTitle>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            Shown at the top of every printed page (Daily Entry report, Worksheet, Schedule).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company-name" className="font-mono text-sm">Company Name</Label>
              <Input
                id="company-name"
                value={profile.company_name}
                onChange={(e) => setProfile(p => ({ ...p, company_name: e.target.value }))}
                placeholder="ABC Lottery LLC"
                className="font-mono"
                data-testid="business-company-name"
              />
            </div>
            <div>
              <Label htmlFor="dba-name" className="font-mono text-sm">DBA (Doing Business As)</Label>
              <Input
                id="dba-name"
                value={profile.dba_name}
                onChange={(e) => setProfile(p => ({ ...p, dba_name: e.target.value }))}
                placeholder="Prime Star Lottery"
                className="font-mono"
                data-testid="business-dba-name"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="business-address" className="font-mono text-sm">Business Complete Address</Label>
              <Input
                id="business-address"
                value={profile.business_address}
                onChange={(e) => setProfile(p => ({ ...p, business_address: e.target.value }))}
                placeholder="123 Main St, Louisville, KY 40202"
                className="font-mono"
                data-testid="business-address"
              />
            </div>
            <div>
              <Label htmlFor="phone-number" className="font-mono text-sm">Phone Number</Label>
              <Input
                id="phone-number"
                value={profile.phone_number}
                onChange={(e) => setProfile(p => ({ ...p, phone_number: e.target.value }))}
                placeholder="502-555-1234"
                className="font-mono"
                data-testid="business-phone"
              />
            </div>
            <div>
              <Label htmlFor="ein" className="font-mono text-sm">EIN (Employer Identification Number)</Label>
              <Input
                id="ein"
                value={profile.ein}
                onChange={(e) => setProfile(p => ({ ...p, ein: e.target.value }))}
                placeholder="12-3456789"
                className="font-mono"
                data-testid="business-ein"
              />
            </div>
          </div>
          <Button
            onClick={saveProfile}
            disabled={savingProfile}
            className="btn-primary flex items-center gap-2"
            data-testid="save-business-info"
          >
            <Save className="w-4 h-4" />
            {savingProfile ? 'Saving...' : 'Save Business Info'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/50" data-testid="user-settings-panel">
        <CardHeader>
          <CardTitle className="font-mono flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Default Pack Sizes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground font-mono">Set the default number of tickets per pack for each game. These are used when no custom qty is entered in the daily entry.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {DENOMINATIONS.map(d => (
              <div key={d} className="flex items-center gap-2">
                <Label className="font-mono text-sm w-10 text-primary font-bold">{DENOM_LABELS[d]}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={packSizes[d]}
                  onChange={(e) => setPackSizes(prev => ({ ...prev, [d]: e.target.value.replace(/[^0-9]/g, '') }))}
                  className="h-9 w-20 text-center text-sm font-mono"
                  data-testid={`user-pack-size-${d}`}
                />
                <span className="text-[10px] text-muted-foreground font-mono">0-{(parseInt(packSizes[d]) || 1) - 1}</span>
              </div>
            ))}
          </div>
          <Button onClick={savePackSizes} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="user-save-pack-sizes">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Pack Sizes'}
          </Button>

          <div className="pt-4 mt-2 border-t border-border/40 space-y-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="show-qty-toggle"
                checked={showQtyCells}
                onCheckedChange={toggleShowQtyCells}
                data-testid="toggle-show-qty-cells"
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="show-qty-toggle" className="font-mono text-sm cursor-pointer">
                  Show pack quantity cells in Daily Entry
                </Label>
                <p className="text-[11px] text-muted-foreground font-mono leading-snug">
                  When unchecked, the tiny qty boxes below each running number are hidden. Defaults from above will be used automatically. Double-click any running number cell to temporarily reveal its qty box for overrides.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change PIN panel — hidden for admins (admins bypass the PIN gate entirely) */}
      {!isAdmin && (
      <Card className="border-border/50" data-testid="change-pin-panel">
        <CardHeader>
          <CardTitle className="font-mono flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Change Settings PIN
          </CardTitle>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            Update the 4-digit PIN that protects this page and the LVM report.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="font-mono text-sm">Current PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinCurrent}
                onChange={(e) => setPinCurrent(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                placeholder="••••"
                className="font-mono text-center tracking-[0.5em]"
                data-testid="pin-current"
              />
            </div>
            <div>
              <Label className="font-mono text-sm">New PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinNew}
                onChange={(e) => setPinNew(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                placeholder="••••"
                className="font-mono text-center tracking-[0.5em]"
                data-testid="pin-new"
              />
            </div>
            <div>
              <Label className="font-mono text-sm">Confirm new PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinConfirm}
                onChange={(e) => setPinConfirm(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                placeholder="••••"
                className="font-mono text-center tracking-[0.5em]"
                data-testid="pin-confirm"
              />
            </div>
          </div>
          <Button
            onClick={changePin}
            disabled={changingPin || pinCurrent.length !== 4 || pinNew.length !== 4 || pinConfirm.length !== 4}
            className="btn-primary flex items-center gap-2"
            data-testid="pin-change-save"
          >
            <ShieldCheck className="w-4 h-4" />
            {changingPin ? 'Updating...' : 'Update PIN'}
          </Button>
        </CardContent>
      </Card>
      )}
    </motion.div>
    </PinLockGate>
  );
}
